// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## CountdownTimer
//
// Include description of object & useful info here, please!

export default class CountdownTimer {
  running = false;

  startMS = 0;

  stopMS = 0;

  durationMS = 0;

  remainingMS = 0;

  intervalID: number | NodeJS.Timer | null = null;

  timeoutCallbacks = [];

  timeText = '0:00';

  displayDiv: HTMLElement;

  // Public constructor
  constructor(displayDiv: HTMLElement) {
    this.init(displayDiv);
  }

  init(displayDiv: HTMLElement) {
    this.displayDiv = displayDiv;
    this.displayDiv.innerHTML = this.timeText;

    return this;
  }

  /**
   * Set the time on the timer
   * @param timeString as "1:20" for one minute & twenty seconds
   * @param accommodationCoefficient number by which to multiply available time for ADA accommodations
   */
  set(timeString: string, accommodationCoefficient = 1) {
    // console.log(`Setting timer w/ accommodationCoefficient of ${accommodationCoefficient}`);
    // Time String in format MM:SS
    const { minutes, seconds } = CountdownTimer.processTimeString(
      timeString,
      accommodationCoefficient
    );
    const MS = minutes * 60 * 1000 + seconds * 1000;
    this.durationMS = MS;
    this.remainingMS = MS;
    this.updateDisplay();

    return this;
  }

  start() {
    this.running = true;
    this.startMS = new Date().getTime();
    this.stopMS = this.startMS + this.remainingMS + 1000;
    this.intervalID = setInterval(() => {
      this.calculateTimes();
      this.updateDisplay();
    }, 500);

    return this;
  }

  stop() {
    this.running = false;
    clearInterval(this.intervalID as number);
    return this;
  }

  calculateTimes() {
    const nowMS = new Date().getTime();
    this.remainingMS = Math.max(this.stopMS - nowMS, 0);
    // If we're out of time, call the callbacks.
    if (this.remainingMS <= 0) this.timeout();
  }

  updateDisplay() {
    const minutes = Math.floor(this.remainingMS / (60 * 1000));
    const seconds = Math.floor((this.remainingMS % (60 * 1000)) / 1000);
    const padding0 = seconds < 10 ? '0' : '';
    const timeText = `${minutes}:${padding0}${seconds}`;
    if (timeText !== this.timeText) {
      this.displayDiv.innerHTML = timeText;
      this.timeText = timeText;
    }
  }

  onTimeout(callback, thisArg?) {
    const timeoutCallbacks = this.timeoutCallbacks;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    timeoutCallbacks.forEach((callback) => {
      if (callback.source === 'CountdownTimer') {
        timeoutCallbacks.splice(timeoutCallbacks.indexOf(callback), 1);
      }
    });

    callback = thisArg ? callback.bind(thisArg) : callback;
    callback.source = 'CountdownTimer';
    this.timeoutCallbacks.push(callback);
  }

  timeout() {
    this.running = false;
    clearInterval(this.intervalID as number);
    this.timeoutCallbacks.forEach((callback) => {
      callback();
    });
  }

  /**
   * Convert a time string to useable numbers.
   * @param timeString as "1:20" for one minute & twenty seconds
   * @param accommodationCoefficient number by which to multiply available time for ADA accommodations
   */
  static processTimeString(
    timeString: string,
    accommodationCoefficient = 1
  ): {
    minutes: number;
    seconds: number;
    totalTimeSeconds: number;
    humanString: string;
    clockString: string;
  } {
    // console.log(`Processing time w/ coefficient of ${accommodationCoefficient}`);

    const timeParts = timeString.split(':');
    const rawMinutes = Number(timeParts[0]) * accommodationCoefficient;
    const rawSeconds = Number(timeParts[1]) * accommodationCoefficient;
    const totalSeconds = rawMinutes * 60 + rawSeconds;
    const minutes = ~~(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    let str = '';
    if (minutes === 1) str += `${minutes} minute`;
    else if (minutes > 1) str += `${minutes} minutes`;
    if (seconds !== 0 && str) str += ' and ';
    if (seconds === 1) str += '1 second';
    if (seconds > 1) str += `${seconds} seconds`;
    if (accommodationCoefficient !== 1) {
      str += ` (this includes an accommodation of ${accommodationCoefficient} times the standard time limit)`;
    }

    return {
      minutes,
      seconds,
      totalTimeSeconds: seconds + minutes * 60,
      humanString: str,
      clockString: timeString,
    };
  }
}
